import {AbsoluteFill, Img, interpolate, useCurrentFrame, Easing} from 'remotion';

/**
 * WikipediaReveal — screenshot + scroll + zoom into highlighted text
 *
 * Voidline signature for source citations / document reveals.
 * Drops music -8dB during the pulse (handled by parent <VolumeDucker/>).
 *
 * @example
 * <WikipediaReveal
 *   screenshotPath="/assets/wiki/mary_celeste_full.png"
 *   scrollFromY={0}
 *   scrollToY={-2400}
 *   zoomTarget={{x: 0.5, y: 0.75, scale: 1.4}}
 *   highlightText="found 400 miles east of the Azores"
 *   highlightColor="#FFE066"
 *   highlightAtFrame={120}
 *   durationInFrames={180}
 * />
 */

type Props = {
  screenshotPath: string;       // full Wikipedia article screenshot, tall (e.g. 1280x4800)
  scrollFromY: number;          // px to start (typically 0 = top)
  scrollToY: number;            // px to end (negative = scroll down)
  zoomTarget?: {                // optional final zoom on a region
    x: number;                  // 0-1 normalized
    y: number;                  // 0-1 normalized
    scale: number;              // final zoom (e.g. 1.4)
  };
  highlightText?: string;       // text overlay to render at zoom target
  highlightColor?: string;      // default '#FFE066'
  highlightAtFrame?: number;    // frame when highlight appears
  durationInFrames: number;
  sourceTagline?: string;       // e.g. "wikipedia.org/Mary_Celeste"
};

export const WikipediaReveal: React.FC<Props> = ({
  screenshotPath,
  scrollFromY,
  scrollToY,
  zoomTarget,
  highlightText,
  highlightColor = '#FFE066',
  highlightAtFrame,
  durationInFrames,
  sourceTagline,
}) => {
  const frame = useCurrentFrame();

  // Two-phase: scroll (0 → 70%), then zoom-in if zoomTarget given (70% → 100%)
  const scrollProgress = interpolate(
    frame,
    [0, durationInFrames * 0.7],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.bezier(0.4, 0, 0.2, 1)}
  );
  const zoomProgress = interpolate(
    frame,
    [durationInFrames * 0.7, durationInFrames],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.bezier(0.33, 1, 0.68, 1)}
  );

  const scrollY = interpolate(scrollProgress, [0, 1], [scrollFromY, scrollToY]);
  const zoom = zoomTarget ? interpolate(zoomProgress, [0, 1], [1, zoomTarget.scale]) : 1;
  const targetX = zoomTarget ? (0.5 - zoomTarget.x) * 100 * (zoom - 1) : 0;
  const targetY = zoomTarget ? (0.5 - zoomTarget.y) * 100 * (zoom - 1) : 0;

  const highlightOpacity =
    highlightAtFrame !== undefined && highlightText
      ? interpolate(frame, [highlightAtFrame, highlightAtFrame + 8], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      : 0;

  return (
    <AbsoluteFill style={{overflow: 'hidden', background: '#fff'}}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translate3d(${targetX}%, calc(${scrollY}px + ${targetY}%), 0) scale(${zoom})`,
          transformOrigin: zoomTarget
            ? `${zoomTarget.x * 100}% ${zoomTarget.y * 100}%`
            : 'center',
          willChange: 'transform',
        }}
      >
        <Img src={screenshotPath} style={{width: '100%', display: 'block'}} />
      </div>

      {/* Highlight text overlay at zoom target */}
      {highlightText && zoomTarget && (
        <div
          style={{
            position: 'absolute',
            left: `${zoomTarget.x * 100}%`,
            top: `${zoomTarget.y * 100}%`,
            transform: 'translate(-50%, -50%)',
            background: highlightColor,
            color: '#0A0A0C',
            padding: '8px 16px',
            fontWeight: 700,
            fontFamily: 'system-ui, sans-serif',
            fontSize: 28,
            opacity: highlightOpacity,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            borderRadius: 4,
          }}
        >
          {highlightText}
        </div>
      )}

      {/* Source URL footer */}
      {sourceTagline && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            right: 24,
            background: 'rgba(0,0,0,0.7)',
            color: '#FFD700',
            padding: '4px 12px',
            fontFamily: 'monospace',
            fontSize: 14,
            borderRadius: 2,
          }}
        >
          {sourceTagline}
        </div>
      )}
    </AbsoluteFill>
  );
};
